/**
 * AtlasHelpModule — an in-game, searchable, tabbed help/manual window.
 *
 * A floating "?" button (bottom-right, always visible) + the H hotkey open a modal
 * that documents controls, movement/camera, the admin world editor, building your
 * own world, the permission model, debug/admin tools, and the game's systems. A
 * search bar filters every section across all tabs. Available to ALL players (not
 * dev-gated) — the permission/editing content explains what admins vs. owners vs.
 * visitors can do. Pure client-side; reads nothing authoritative.
 */
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { App, IModule } from 'enginex';

interface HelpSection { title: string; body: string; }
interface HelpTab { id: string; label: string; sections: HelpSection[]; }

// ─── Content ────────────────────────────────────────────────────────────────────
// `body` is trusted static HTML authored here (no user input) — safe to innerHTML.

const TABS: HelpTab[] = [
  {
    id: 'overview',
    label: 'Overview',
    sections: [
      {
        title: 'What is Cosmos?',
        body: `Cosmos is a persistent social multiverse. Your identity — the <b>Cosmos Passport</b>
        (avatar, wallet, reputation, social graph) — is invariant across every world you visit.
        The world is <b>server-authoritative</b>: the server owns all game truth (your position,
        what you own, currency, permissions); your client renders and predicts but never decides.`,
      },
      {
        title: 'Kinds of world',
        body: `<ul>
        <li><b>The Commons</b> — the single shared, platform-owned shard that anchors identity and
        commerce: a 10.24×10.24 km landscape of six procedural biomes with ~60 settlements
        (3 cities, 12 towns, 45 hamlets) and wilderness between them. You interact here but don't
        freely build (unless you're granted admin).</li>
        <li><b>Your Personal World</b> — a 64×64 m instance you own, seeded at signup. You have full
        build rights here — and you can create <i>more</i> worlds of your own any time (<kbd>M</kbd>).</li>
        <li><b>Group Worlds</b> — organization-owned, governed by a Charter.</li>
        <li><b>Arcade Worlds</b> — matchmade competitive instances with leaderboards.</li>
        </ul>`,
      },
      {
        title: 'Getting around',
        body: `Walk with <b>WASD</b> + mouse-look (click the view to capture the mouse). Every panel is
        also one click away on the <b>menu bar</b> (bottom-center) — hotkeys are listed in
        each button's tooltip, and the <b>≡</b> chip collapses the bar. Open this help any time with
        <b>H</b>, the <b>?</b> button, or the menu bar. Admins get a creative fly camera and a full
        world-building editor (see the <i>Admin World Editor</i> tab). The debug console lives on the
        <b>backtick</b> (<code>\`</code>) key / the Debug button on the menu bar (dev-gated).`,
      },
      {
        title: 'Your starter kit & the Inventory (F1)',
        body: `The first time you join Cosmos you are handed a <b>starter kit</b>: a <b>vibro knife</b> and a
        <b>scrap pistol</b> (equip them from the inventory — <kbd>F</kbd> or a pointer-locked click attacks,
        <kbd>Q</kbd> cycles weapons), a <b>field tool</b> (equip it: every hand-sample of a resource yields
        more), a packed <b>Field Extractor Rig</b> deed (Place it on a resource hotspot — survey first — and it
        mines for you while you are away), a <b>Dust Skiff</b> hovercraft (Ride it from the inventory, or
        <kbd>X</kbd> in the open), and a <b>Lancer</b> starter ship in your hangar (fly it from a spaceport
        or board it in the field with <kbd>X</kbd>). Press <kbd>F1</kbd> — or the <b>Inventory</b> button on
        the menu bar — for your items, resources, deeds, ships, equipped gear and the schematics you hold.
        <i>Pre-alpha:</i> this build is not the final game — expect placeholder art, bugs and resets.`,
      },
      {
        title: 'Crafting Guide (F10), schematics & dealers',
        body: `The <b>Crafting Guide</b> (<kbd>F10</kbd>, the <b>Recipes</b> menu button, or GUIDE inside the
        Workbench) lists every design in the game with its full recipe and, against what you hold, whether you
        can craft it <b>right now</b> — READY (craft it), MISSING (the red slots say what to gather or make
        first), or LOCKED. <b>Basic work is open to everyone</b>: every component and every item up to
        complexity 3 (ingots, plates, weaves, cells, field tools, rations, furniture, the first guns and
        blades, the Dust Skiff…). <b>Complex designs</b> — complexity 4 and up: rig cores, ship components,
        droids, the tier-3+ weapons, the faster skimmers, industrial rigs — and <b>every ship hull</b> need
        a <b>schematic</b> in hand. Two ways to get one: <b>buy it from a schematic dealer</b> — the smith (or
        merchant) of any city or town keeps a seasonal board of designs (Workbench → Market, within 400 m of
        the square; the board turns every 6 hours and the dealer pockets the credits) — or
        <b>reverse-engineer</b> a finished piece or a hull you own (Inventory → Reverse engineer): the item
        is <b>destroyed</b> and the design is yours for good. Ship hulls crafted at the bench land in your
        hangar like a bought one.`,
      },
      {
        title: 'Waypoints (maps M / N)',
        body: `<b>Right-click</b> anywhere on the local map (<b>M</b>) or the World Atlas (<b>N</b>) to
        set a named <b>waypoint</b> — a gold <span style="color:#ffd166">◆</span> that shows on both
        maps with its name (out-of-view markers hug the local map's edge so you can walk toward
        them). <b>Right-click a waypoint</b> to rename it, or clear the text to delete it. Waypoints
        are <b>planet-scoped</b>: one set on the Commons never shows on a personal world's map or any
        other planet — only where you planted it. They're yours alone, they persist across sessions,
        and you can keep up to 64.`,
      },
      {
        title: 'The Merchant Hive (F9)',
        body: `A default commerce planet in the commons solar system: flat artificial brick-and-steel
        ground and one mega city of shops of every size — currently a bare hive to explore (no deed
        placement there). Two ways in: press <b>F9</b> (or the <b>Hive</b> menu-bar button) to
        teleport — your exact spot is remembered, and the <b>↩ Return</b> chip (or F9 again) puts you
        back where you were, whenever you like. Or fly: the Hive hangs as a planet on the space
        chart — warp to it and make planetfall like any world.`,
      },
    ],
  },
  {
    id: 'controls',
    label: 'Controls',
    sections: [
      {
        title: 'Movement & view',
        body: `<table>
        <tr><td><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd></td><td>Move (camera-relative)</td></tr>
        <tr><td><kbd>Mouse</kbd></td><td>Look — click the viewport to capture (pointer lock); <kbd>Esc</kbd> releases</td></tr>
        <tr><td><kbd>Space</kbd></td><td>Jump (walking) · ascend (flying)</td></tr>
        <tr><td><kbd>Tab</kbd></td><td>Toggle between the Commons and your personal world</td></tr>
        </table>`,
      },
      {
        title: 'Panels',
        body: `Every panel below is ALSO a button on the bottom menu bar — keys are optional. Panels
        open as <b>desktop-style windows</b>: drag the title bar to move, drag the corner
        grip to resize, double-click the title (or the — button) to roll up, the droplet cycles the
        window's opacity, and × closes. Each window remembers its position, size, and opacity.
        <table>
        <tr><td><kbd>H</kbd> / <kbd>?</kbd></td><td>This help window</td></tr>
        <tr><td><kbd>\`</kbd></td><td>Debug console (dev/admin)</td></tr>
        <tr><td><kbd>C</kbd></td><td>Social Compass (your Want/Fear)</td></tr>
        <tr><td><kbd>O</kbd></td><td>Orbits roster — <i>also</i> the editor Outliner in fly mode (context-dependent)</td></tr>
        <tr><td><kbd>L</kbd></td><td>World Logic (visual scripting)</td></tr>
        <tr><td><kbd>G</kbd></td><td>Forge Canvas (collaborative building)</td></tr>
        <tr><td><kbd>P</kbd></td><td>Passport / avatar editor</td></tr>
        <tr><td><kbd>M</kbd></td><td>My Worlds — create, share, and hop between your worlds</td></tr>
        <tr><td><kbd>F3</kbd></td><td>Performance HUD (FPS + draw calls)</td></tr>
        <tr><td><kbd>Esc</kbd></td><td>Graphics / options menu (or close the focused panel)</td></tr>
        </table>`,
      },
      {
        title: 'EDIT MODE (F2) — one screen for everything',
        body: `Press <kbd>F2</kbd> (or the <b>Edit</b> button) in a world you own, hold a build grant in,
        or — on the Commons — administer, and the world switches from <i>exploring</i> to <i>editing</i>:
        you <b>fly</b> (WASD, Space/C up/down, Shift boost, <b>hold right-mouse to look</b>), the cursor is
        free for picking, and every tool sits on one toolbar with one context panel — no more windows.
        <table>
        <tr><td><kbd>1</kbd></td><td><b>Select</b> — click an object; Delete removes, Ctrl+D duplicates, Ctrl+Z / Ctrl+Y undo & redo (one stack for everything)</td></tr>
        <tr><td><kbd>2</kbd><kbd>3</kbd><kbd>4</kbd></td><td><b>Move / Rotate / Scale</b> gizmos; <kbd>X</kbd> toggles snapping (1 m · 15° · 0.25)</td></tr>
        <tr><td><kbd>5</kbd></td><td><b>Place</b> — shapes, the prefab catalog, your uploads, your deeds; a green ghost follows the ground, click to place, <kbd>R</kbd> rotates</td></tr>
        <tr><td><kbd>6</kbd></td><td><b>Sculpt</b> the terrain — LMB raises, Ctrl+LMB lowers, Alt+LMB flattens; radius & strength sliders; undo last stroke / reset</td></tr>
        <tr><td><kbd>7</kbd></td><td><b>Paint</b> the ground — pick a layer (the six biome grounds on the Commons; regolith/moss/dirt in your world) and brush</td></tr>
        <tr><td><kbd>8</kbd></td><td><b>Interior</b> — select a building and flag it <b>enterable</b>: a door, floors, stairs and walls are carved from its size; walk in (E) and furnish it</td></tr>
        <tr><td><kbd>9</kbd></td><td><b>Script</b> — select a part and write what happens when a player presses E on it (Lua, the <code>on_interact</code> hook); open the full IDE from there</td></tr>
        <tr><td><kbd>0</kbd></td><td><b>Upload</b> a .glb — when it's validated it appears in Place → My uploads</td></tr>
        <tr><td><kbd>-</kbd></td><td><b>NPC</b> — the NPC creator: place a blank-slate character (pick a body, name it) and write its Lua against the <code>npc.*</code> API — senses (who's near, eye contact, speed, mode), movement, gestures, a state label, speech bubbles, topics &amp; choices in a talk window, per-player memory, environment (time/weather/biome), world conditions, signals between scripts, and asking versonas questions. Templates + the full reference live in the panel</td></tr>
        <tr><td><kbd>[</kbd></td><td><b>Environment</b> — this world's time of day (running or held), day length, weather (rotating or fixed), fog density &amp; tint, sun/ambient brightness &amp; tint, and the ambience sound bed — applied for every visitor</td></tr>
        <tr><td><kbd>]</kbd></td><td><b>Audio</b> — place ambience / music / sfx zones (pick a clip, radius, gain, loop, fade); they render as wire spheres while editing, the Scale gizmo resizes them</td></tr>
        <tr><td><kbd>;</kbd></td><td><b>Devices</b> — things that DO something, wired by <b>signals</b> ("name:event"): lights (point/spot, flicker), particle emitters (fireflies, smoke, sparks, embers, rain, fountain, snow, dust), signs/holograms, doors (swing/slide/lift, locks, auto-close), buttons &amp; levers, trigger volumes, teleporters (server-moved), seats, moving platforms &amp; spinners (solid, they carry you), jump pads, gravity zones, kill/respawn zones, checkpoints, spawn points (where visitors arrive), speakers, water volumes (swim under them), cinematic camera paths (fly to each shot, "+ Keyframe", ▶ Preview; E / a signal / <code>world.play_cinematic</code> plays it with letterbox + fade, Esc skips), NPC paths (patrol routes), scoreboards and timers. Select one for its settings; "Test (trigger)" fires it</td></tr>
        <tr><td>Rules</td><td>In the <b>Environment</b> panel: an objective line, a round timer, respawn at spawn/checkpoint, and a score signal the HUD counts — optional, for worlds that want a game in them</td></tr>
        <tr><td>NPC data</td><td>In the <b>NPC</b> panel: <b>presets</b> (shopkeeper, guard, guide, quest giver), a <b>dialogue-tree editor</b> (nodes of NPC text → player choices → next node; "only if memory" conditions; actions like <code>mem quest=1</code>, <code>signal gate:open</code>, <code>state busy</code>, <code>say …</code>, <code>emote wave</code>, <code>goto x z</code>) and a <b>day schedule</b> (hour → walk / home / wander / state / follow path / say). NPC walks route around parts and solid devices (grid A*), and <code>npc.follow_path("name")</code> patrols an NPC path device</td></tr>
        <tr><td>Shift+click · drag</td><td><b>Multi-select</b> — Shift-click adds/removes an object, dragging a box over empty space rubber-bands several; Move/Rotate/Scale then act on the whole group about its centroid as ONE undo step. The <b>outliner</b> lists everything placed (filterable; click selects), the <b>inspector</b> takes exact X/Y/Z/yaw/scale and a physics toggle, <b>Drop to floor</b> seats the selection on the ground, and <b>Save as prefab</b> keeps a selection of parts as a reusable prefab (Place → My prefabs)</td></tr>
        <tr><td><kbd>=</kbd></td><td><b>Versona</b> — place a living versona. On the Commons it becomes a settler with the full Commons context (society, economy, memories). In your own world you author it: name, characteristics, what it knows, topics, and determinism knobs (LLM on/off, strict answers-only-from-context, temperature, length, wander)</td></tr>
        <tr><td><kbd>Esc</kbd></td><td>Deselect → cancel → exit back to exploring</td></tr>
        </table>
        Sculpting and paint are real and shared: every visitor sees your hills and ground. Everything is
        server-checked — the button only shows where you actually hold the right. The older windows
        (<kbd>B</kbd> Build Mode, <kbd>G</kbd> Forge Canvas, <kbd>F7</kbd> Uploads) now open Edit Mode.`,
      },
      {
        title: 'Personal building & scripting',
        body: `Active in <b>your own personal world</b> (build edit) and anywhere (the IDE).
        <table>
        <tr><td><kbd>B</kbd></td><td>Toggle <b>build-edit</b> mode in your own personal world — frees the cursor so parts are clickable</td></tr>
        <tr><td><kbd>Left-click</kbd></td><td>(build edit) Select the part under the cursor — a transform gizmo attaches</td></tr>
        <tr><td><kbd>1</kbd>/<kbd>2</kbd>/<kbd>3</kbd></td><td>(build edit) Gizmo mode: move / rotate / scale</td></tr>
        <tr><td><kbd>X</kbd></td><td>(build edit) Toggle snapping (1 m / 15° / 0.25)</td></tr>
        <tr><td><kbd>Delete</kbd></td><td>(build edit) Delete the selected part</td></tr>
        <tr><td><kbd>Esc</kbd></td><td>(build edit) Deselect — press again to exit build edit</td></tr>
        <tr><td><kbd>I</kbd></td><td>Toggle the <b>Lua IDE</b> — <b>in Edit Mode only</b> (F2; also while a droid terminal is open): contracts, world scripts and NPC scripts (see <i>Building Your World</i>)</td></tr>
        </table>
        <i>Note:</i> <kbd>B</kbd> is context-dependent — in an admin fly session it opens the prefab
        palette instead (below).`,
      },
      {
        title: 'Admin world editor',
        body: `Only active for admins in fly mode.
        <table>
        <tr><td><kbd>F</kbd></td><td>Toggle the creative fly camera</td></tr>
        <tr><td><kbd>B</kbd></td><td>Open the prefab palette</td></tr>
        <tr><td><kbd>Left-click</kbd></td><td>Place (on ground, with a brush) or select a placement</td></tr>
        <tr><td><kbd>1</kbd>/<kbd>2</kbd>/<kbd>3</kbd></td><td>Gizmo mode: move / rotate / scale</td></tr>
        <tr><td><kbd>X</kbd></td><td>Toggle snapping (1 m / 15° / 0.25)</td></tr>
        <tr><td><kbd>Shift</kbd>+<kbd>D</kbd></td><td>Duplicate the selected object</td></tr>
        <tr><td><kbd>Ctrl</kbd>+<kbd>C</kbd> / <kbd>Ctrl</kbd>+<kbd>V</kbd></td><td>Copy / paste (paste at the crosshair)</td></tr>
        <tr><td><kbd>O</kbd></td><td>Outliner (list every placement)</td></tr>
        <tr><td><kbd>Ctrl</kbd>+<kbd>Z</kbd></td><td>Undo (place + transform)</td></tr>
        <tr><td><kbd>Delete</kbd></td><td>Delete the selected object</td></tr>
        <tr><td><kbd>Esc</kbd></td><td>Deselect</td></tr>
        </table>`,
      },
    ],
  },
  {
    id: 'movement',
    label: 'Movement & Camera',
    sections: [
      {
        title: 'Walking (everyone)',
        body: `You control an avatar in first person. <b>WASD</b> sets a movement <i>intent</i>; the
        server integrates it at a fixed speed and writes your authoritative position — the client
        predicts locally for responsiveness and reconciles if the server disagrees. <b>Mouse-look</b>
        needs pointer lock: click the viewport to capture the cursor, <kbd>Esc</kbd> to release.
        Physics (a Rapier character controller) handles walls, slopes, steps, and gravity.`,
      },
      {
        title: 'VR (Meta Quest)',
        body: `On a WebXR browser (Quest 3's browser, or a desktop headset runtime) an
        <b>Enter VR</b> button appears bottom-right once you're in a world. In VR the headset owns the
        camera: <b>left stick</b> moves (head-relative, through the same server-validated path as
        WASD), <b>right stick</b> snap-turns ±30°, and <b>A</b> jumps. Press <b>X</b> (left
        controller) for the <b>in-headset panel</b> — point at it with either controller and click
        with the trigger. Three tabs: <b>Main</b> (go home · go to the Commons · exit VR),
        <b>My Worlds</b> (one-click Enter into any world you own), and <b>HUD</b> (wallet, the four
        Pulse needs as live bars, in-world time, players sharing the world). The footer mirrors the
        latest status message on every page, so server feedback stays visible in VR. Exit via Main,
        the Enter-VR button, or the headset's system gesture. Serving note (dev): WebXR needs a
        secure context — on-device use
        <code>adb reverse</code> + <code>http://localhost</code>, or HTTPS on the LAN.`,
      },
      {
        title: 'Fly camera (admin)',
        body: `Admins can press <b>F</b> (or <code>/fly</code>) to enter a creative fly camera:
        <b>WASD</b> flies along your look direction, <b>Space</b>/<b>Ctrl</b> ascend/descend, <b>Shift</b>
        boosts. While flying, your avatar <b>parks</b> — nothing is sent to the server, so other players
        see you stand still, and the camera is decoupled from your character. Press <b>F</b> again to
        return the camera to your avatar.`,
      },
    ],
  },
  {
    id: 'editor',
    label: 'Admin World Editor',
    sections: [
      {
        title: 'What it is',
        body: `A creative in-game level editor for admins to hand-design the Commons cities: fly around,
        place structures and props from a palette, and move/rotate/scale them with gizmos — all
        <b>persisted server-side</b> (they survive logout and server restarts). Every change is an
        admin-capability-gated server reducer; non-admins are rejected server-side.`,
      },
      {
        title: 'Becoming an admin',
        body: `The Commons is platform-owned, so no player is its owner by default. Bootstrap yourself:
        open the console (<kbd>\`</kbd>), run <code>/grant-admin</code>, then <b>reload</b>. This grants
        your identity the <b>admin</b> capability in the Commons. (Safety: only the <i>first</i> caller
        can claim admin — once an admin exists, other identities are refused.)`,
      },
      {
        title: 'Placing objects',
        body: `Enter fly mode (<b>F</b>), press <b>B</b> to open the palette, and click a prefab (search
        the ~60 structures/props). Aim the center crosshair at the ground and <b>left-click</b> to place
        it there — Minecraft-style aim + click. The object appears instantly and is saved to the
        database. Unregistered prefabs render as an orange wireframe box so nothing is invisible.`,
      },
      {
        title: 'Transforming objects',
        body: `<b>Left-click</b> a placement to select it: the cursor frees, a green gizmo appears, and a
        numeric panel opens (bottom-left). <b>Drag</b> the gizmo handles or <b>type exact values</b>
        (position / rotation° / scale X-Y-Z, plus a uniform-scale field). Switch gizmo mode with
        <b>1</b> (move) / <b>2</b> (rotate) / <b>3</b> (scale). <b>X</b> toggles snapping. Changes save
        on drag-release and on numeric commit (not every frame). Scale is non-uniform and clamped to
        0.01–100 per axis.`,
      },
      {
        title: 'Power tools',
        body: `<ul>
        <li><b>Shift+D</b> — duplicate the selection (offset 2 m).</li>
        <li><b>Ctrl+C</b> / <b>Ctrl+V</b> — copy the selection's prefab + rotation + scale, paste at the
        crosshair ground point.</li>
        <li><b>O</b> — the outliner: a searchable list of every placement; click a row to select it and
        fly the camera to frame it.</li>
        <li><b>Ctrl+Z</b> — undo a place or a transform (last-in-first-out, up to 50 steps).</li>
        <li><b>Delete</b> — remove the selection.</li>
        </ul>`,
      },
      {
        title: 'Naming cities',
        body: `Each of the ~60 Commons settlements (3 cities, 12 towns, 45 hamlets across six biomes)
        has a default name. Rename one with
        <code>/name-city &lt;id&gt; &lt;new name&gt;</code> (find ids via <code>/list-cities</code>) — the
        name persists. Then <code>/tp-city &lt;name&gt;</code> teleports there.`,
      },
      {
        title: 'How persistence works',
        body: `Placements live in a server table (<code>admin_placement</code>) with full transform +
        non-uniform scale. Your client <b>subscribes</b> to it and renders each row through the normal
        prefab→asset pipeline. Because the server is authoritative, every connected client sees the same
        placements, and they reload identically after any restart.`,
      },
    ],
  },
  {
    id: 'building',
    label: 'Building Your World',
    sections: [
      {
        title: 'Your personal world (no admin needed)',
        body: `Every account owns a 64×64 m <b>personal world</b>, seeded at signup. You have full build
        rights there — no admin capability required, because you're the <b>owner</b>. Switch to it with
        <kbd>Tab</kbd>. Building in your own world uses the <b>Freeform</b> system, not the admin editor
        (which is Commons-only).`,
      },
      {
        title: 'More worlds (M)',
        body: `Press <kbd>M</kbd> for <b>My Worlds</b>: create as many additional worlds as you like —
        name each one, pick a size (64–512 m) and a visibility (<b>public</b> — appears in Discovery ·
        <b>unlisted</b> — enter by invite/link · <b>private</b>). Creating charges a one-time
        <b>SOV hosting fee</b>, and you are the new world's <b>owner</b> — full build + admin rights,
        exactly like your personal world. The panel's per-world <b>Access</b> editor grants other
        players <b>build</b> / <b>decorate</b> / <b>script</b> roles <i>by username</i> (and revokes
        back to visitor). Building UI (the Build tab, <kbd>B</kbd> edit mode) only appears in worlds
        where you actually hold build rights.`,
      },
      {
        title: 'Freeform building',
        body: `In a world you can build in, rez primitives and cooked models, then edit their transform.
        Placed parts are budgeted by <b>Land Impact (LI)</b> — each part costs LI against your world's
        budget, so worlds stay performant. You place from a catalog of prefabs; move/rotate/scale each
        part; link parts into linksets; and delete them. Everything is server-persisted and
        permission-checked.`,
      },
      {
        title: 'In-world editing (B)',
        body: `Press <kbd>B</kbd> in your own personal world to enter <b>build-edit</b> mode: the cursor
        frees, and <b>clicking any placed part</b> attaches a transform gizmo. Drag the handles (or
        switch modes with <kbd>1</kbd>/<kbd>2</kbd>/<kbd>3</kbd>, snap with <kbd>X</kbd>); the edit
        saves on drag-release through the same server-validated path as the Build tab — the server
        re-checks your build capability and the <b>LI budget</b> on every edit, and an over-budget edit
        snaps the part back. <kbd>Delete</kbd> removes the selection; <kbd>Esc</kbd> deselects, then
        exits. Physics parts' colliders follow your edits automatically.`,
      },
      {
        title: 'Scripting your world (Lua IDE — I, inside Edit Mode)',
        body: `The <b>Lua IDE</b> is an <b>Edit Mode</b> surface: press <kbd>F2</kbd>, then <kbd>I</kbd>
        (or the Script / NPC tool's <i>Open in Lua IDE</i> button) — it opens over the editor on the script
        you picked and closes when you leave Edit Mode. Outside Edit Mode <kbd>I</kbd> only reminds you;
        the one exception is an open <b>droid terminal</b> (<kbd>K</kbd>).
        <br><br>Three script kinds live in the left rail: <b>⚙ server contract</b> (compiled on the server
        into a signed, sandboxed <i>Behavioral Contract</i> — capabilities declared by you, granted by the
        owner in <kbd>L</kbd>; CPU/memory budgets throttle rather than trust), <b>world scripts</b>
        (client-side Lua every visitor runs — props, motion, labels, reactions to contract events) and
        <b>☺ NPC scripts</b> (one per scripted NPC from the NPC tool — the <code>npc.*</code> API below).
        Each rail row has an <i>enabled</i> box; <b>Deploy</b> (Ctrl-S) publishes, <b>▶ Run</b> previews
        locally (an NPC tab previews on its own unit — the live script stands down until you stop).
        <br><br><b>API reference</b> (the <i>API</i> side panel): every function grouped by kind —
        hooks · NPC identity/state · movement &amp; gestures · player detection · dialogue · environment ·
        world conditions &amp; signals · versona interaction · world props/FX · environment · players ·
        queries · UI/storage. Click a group chip to focus it, fold a group by its header, or type in the
        search box (names <i>and</i> documentation are searched — "eye contact", "speech bubble", "fog").
        Click an entry to insert it at the cursor; the same catalog drives autocomplete, hover docs and the
        unknown-call lint, so they never disagree.
        <br><br><b>Copilot</b> (the <i>AI</i> side panel, Ctrl-I for inline edits): it is briefed with the
        full API, the rules of each script kind, the starter templates, every script in the world, the
        build parts, the scripted NPCs and the versonas present. In <b>agent</b> mode it can
        <code>list_scripts</code> / <code>read_script</code> (world and NPC), <code>list_npcs</code>
        (live state, topics, versona ids), <code>list_objects</code>, <code>search_api</code>,
        <code>lint_source</code>, and — the real test loop — <code>run_script</code>: it runs the buffer in
        the live sandbox, optionally <i>bound to an NPC</i> and with <i>synthetic events</i> injected
        ("a player walked up at 0.5 s, said hello at 1 s, clicked a topic at 2 s"), then reads back a report:
        hooks called with counts, Lua errors, console lines, and the NPC's before/after snapshot plus its
        action log (what it said, where it walked, which topics it added). Edits are always <i>staged</i> as
        a reviewable diff — you accept chunks and deploy; the copilot never writes to the server.`,
      },
      {
        title: 'World scripts — client-side Lua (games & digital twins)',
        body: `The IDE's <b>Deploy as</b> selector has a second target: a <b>World script</b> — Lua
        that ships to <i>every visitor</i> of your world and runs on their client inside the EngineX
        <b>sandbox</b> (isolated environment, read-only standard library, a 3-strike error budget, and
        a slow-script watchdog). Use it for everything experiential — props, motion, labels,
        ambience — while <i>authoritative</i> shared state stays in server contracts. The two meet
        through events: a contract's <code>world.emit("door.opened")</code> reaches every client's
        <code>on_event</code>, where your world script animates the door.
        <br><br>Shape: <code>return { on_init = fn, on_tick = fn(dt), on_event = fn(type, payload) }</code>.
        The <code>world.*</code> bridge: <code>spawn(prefabKey,x,y,z)→h</code> ·
        <code>set(h,x,y,z[,yaw[,scale]])</code> · <code>tween(h,x,y,z,secs)</code> (smooth motion —
        doors, elevators, patrols) · <code>remove(h)</code> · <code>label(text,x,y,z)→h</code> ·
        <code>particles(x,y,z[,n[,hexColor[,speed]]])</code> (one-shot burst, identical on every
        client) · <code>tone(freq,ms[,vol])</code> (clamped beep) · <code>player_count()</code> /
        <code>player_name(i)</code> / <code>player_x|y|z(i)</code> · <code>my_name()</code> /
        <code>my_x|y|z()</code> (the local player — proximity triggers) ·
        <code>terrain_height(x,z)</code> · <code>time_of_day()</code> · <code>log(msg)</code>.
        Limits: 16 scripts/world, 64 KB each, 256 props per script. The bridge is a <i>curated</i>
        surface built on the engine — visiting clients never expose their raw engine internals to
        someone else's world, which is what keeps foreign worlds safe to walk into.`,
      },
      {
        title: 'NPC scripts — the npc.* API (Edit Mode → NPC)',
        body: `A scripted NPC is a <b>blank slate</b>: a body, a spot, and Lua. It knows only what its
        script reads — no versona brain, no society. The script <b>returns a table of hooks</b> and runs on
        every visitor's client, so "the player" is whoever is looking.
        <br><br><b>Hooks:</b> <code>on_init</code> · <code>on_tick(dt)</code> ·
        <code>on_player_enter(name, dist)</code> / <code>on_player_leave(name)</code> (the sense radius,
        <code>npc.set_sense</code>) · <code>on_interact(name, dist)</code> (E nearby) ·
        <code>on_talk_start/on_talk_end(name)</code> · <code>on_say(name, text)</code> (typed text) ·
        <code>on_topic(key, name)</code> · <code>on_choice(index, label, name)</code> ·
        <code>on_arrive(x, z)</code> · <code>on_signal(topic, payload, from)</code> ·
        <code>on_versona_reply(id, text)</code> · <code>on_hour(hour)</code> · <code>on_event</code>.
        <br><br><b>Senses:</b> <code>npc.player_dist/near(r)/facing_me(deg)/moving/speed/mode/is_owner/met</code>,
        <code>npc.players_near(r)</code>. <b>State:</b> <code>npc.set_state("patrolling")</code> (shown under
        the nameplate; other NPCs read it with <code>npc.other_state(id)</code>), <code>npc.config(key)</code>
        (the Config JSON knobs), <code>npc.memory_set/get</code> (remembers each player on their device).
        <b>Movement &amp; body:</b> <code>npc.goto(x, z, speed)</code> (ground-following, fires
        <code>on_arrive</code>), <code>wander(r)</code>, <code>home()</code>, <code>face_player()</code>,
        <code>emote("wave"|"nod"|"bow"|"spin"|"jump")</code>, <code>tint</code>, <code>hide/show</code>.
        <b>Dialogue:</b> <code>npc.say(text[, secs])</code> (speech bubble + the talk window),
        <code>set_greeting</code>, <code>add_topic(key, label[, reply])</code> (a reply answers itself;
        without one <code>on_topic</code> fires), <code>ask(question, a, b, …)</code> → <code>on_choice</code>,
        <code>set_talkable</code>, <code>open_talk/end_talk</code>. <b>Environment:</b>
        <code>hour/is_night/time_of_day/weather/biome/slope/in_water/ground_y</code>.
        <b>World conditions:</b> <code>part_near(r)/nearest_part</code>, <code>player_count</code>,
        <code>war_active</code>, <code>nearest_town</code>, <code>timer(name, secs)</code> (true once per
        period — call it in <code>on_tick</code>), <code>chance/random</code> (per-NPC seeded),
        <code>signal(topic, payload)</code> (every script's <code>on_signal</code> fires — NPC↔NPC wiring).
        <b>Versonas:</b> <code>nearest_versona</code>, <code>versona_name/x/z/state/job/mood/dist(id)</code>,
        <code>goto_versona(id)</code>, <code>versona_ask(id, text)</code> → the living NPC thinks and answers
        in <code>on_versona_reply</code>.
        <br><br>Start from a template (Greeter · Patrol guard · Quest giver · Versona liaison · Blank), test with
        ▶ Run or the copilot's <code>run_script</code>, and watch the console: <code>world.log</code>, Lua
        errors and the preview summary ("hooks: on_init×1 on_tick×150; errors: 0") all land there.`,
      },
      {
        title: 'Forge Canvas (collaborative)',
        body: `Press <b>G</b> to open <b>Canvas Mode</b> — real-time collaborative building with terrain
        editing and object placement, multi-cursor presence, and snapshot-backed <b>undo</b>. Who can
        build is governed by the world's <b>Permission Matrix</b> (see the Permissions tab): the owner and
        admins always can; others need a <b>build</b> or <b>decorate</b> grant. Same-cell edits resolve
        deterministically (last writer wins, with a bumped version), and undo applies the inverse for
        everyone.</li>`,
      },
      {
        title: 'Admin editor vs. Freeform vs. Forge',
        body: `<ul>
        <li><b>Admin World Editor</b> — Commons only, admin-gated, for designing the shared cities.
        Fly + palette + gizmos + persistence.</li>
        <li><b>Freeform</b> — your own (or any build-granted) world; LI-budgeted parts + catalog models,
        with in-world gizmo editing on <kbd>B</kbd>.</li>
        <li><b>Forge Canvas</b> (<b>G</b>) — collaborative, Permission-Matrix-gated, terrain + objects
        + multi-cursor + undo.</li>
        <li><b>Lua IDE</b> (<kbd>I</kbd>) — scripts <i>behavior</i> rather than placement; server-side
        contracts, capability-gated.</li>
        </ul>`,
      },
    ],
  },
  {
    id: 'permissions',
    label: 'Permissions',
    sections: [
      {
        title: 'World capabilities',
        body: `Access to a world is a set of <b>capabilities</b> on a per-(world, identity) role:
        <table>
        <tr><td><b>enter</b></td><td>Be present in the world (visitors have only this)</td></tr>
        <tr><td><b>build</b></td><td>Place/edit terrain + objects</td></tr>
        <tr><td><b>decorate</b></td><td>Place/move decorative objects</td></tr>
        <tr><td><b>script</b></td><td>Author World Logic contracts</td></tr>
        <tr><td><b>take</b></td><td>Take/pick up objects</td></tr>
        <tr><td><b>admin</b></td><td>Full control incl. granting roles (implies all)</td></tr>
        </table>
        The <b>owner</b> of a world implicitly holds every capability. Everyone else is a <b>visitor</b>
        (enter-only) until granted a role. The server re-checks the capability on <i>every</i> mutating
        action — the client UI only reflects it.`,
      },
      {
        title: 'Commons vs. personal',
        body: `The <b>Commons</b> is owned by the platform (a zero identity), so no player is its owner —
        which is why admin there must be explicitly granted (<code>/grant-admin</code> for the dev
        bootstrap). Your <b>personal world</b> is owned by you, so you already hold every capability
        there — build freely without any grant.`,
      },
      {
        title: 'Item permissions (Provenance)',
        body: `Objects you own carry five permission flags enforced server-side across worlds:
        <b>Copy</b>, <b>Modify</b>, <b>Transfer</b>, <b>Remix</b> (derive a new work), <b>Template</b>
        (instantiate copies). The <b>creator chain</b> is immutable — a remix is a new asset linked to
        its parent. On transfer, the recipient gets the <i>intersection</i> of the asset's perms and the
        grant, so you can never pass on more than you hold.`,
      },
    ],
  },
  {
    id: 'debug',
    label: 'Debug & Admin',
    sections: [
      {
        title: 'The debug console',
        body: `Press <kbd>\`</kbd> (backtick) to open the dev/admin panel. It's <b>dev-gated</b> — present
        only on a local/dev origin (localhost) or when <code>localStorage['atlas:debug'] = '1'</code>.
        It hosts a command console plus Weather and Day/Night controls (each with a <i>Local</i>
        view-only / <i>Shared</i> whole-Commons switch), and a settlement mesh-name toggle.`,
      },
      {
        title: 'Console commands',
        body: `<table>
        <tr><td><code>/tp &lt;x&gt; &lt;z&gt;</code></td><td>Teleport to coordinates</td></tr>
        <tr><td><code>/tp-home</code></td><td>Teleport to origin</td></tr>
        <tr><td><code>/tp-biome &lt;biome&gt;</code></td><td>Nearest settlement of a biome theme</td></tr>
        <tr><td><code>/tp-settlement &lt;id&gt;</code></td><td>Settlement by id</td></tr>
        <tr><td><code>/tp-city &lt;name&gt;</code></td><td>Named city (default or admin-set)</td></tr>
        <tr><td><code>/list-biomes</code> · <code>/list-settlements</code> · <code>/list-cities</code></td><td>List them (with ids + distances)</td></tr>
        <tr><td><code>/name-city &lt;id&gt; &lt;name&gt;</code></td><td>Rename a city (admin, persisted)</td></tr>
        <tr><td><code>/fly</code></td><td>Toggle the fly camera</td></tr>
        <tr><td><code>/grant-admin</code></td><td>Bootstrap yourself as Commons admin (first caller only)</td></tr>
        <tr><td><code>/help</code></td><td>List commands in the console</td></tr>
        </table>`,
      },
      {
        title: 'Admin vs. debug mode',
        body: `<b>Debug mode</b> is a <i>client</i> gate (localhost / the debug flag) that reveals the
        debug panel + the world editor UI — it does not grant any server power. <b>Admin</b> is a
        <i>server</i> capability (the <b>admin</b> role on a world). You can see the editor UI in debug
        mode, but placing/transforming objects requires the server admin capability — which is why
        <code>/grant-admin</code> exists. In a real deployment the debug reducers are removed/gated; on
        your local host they're a convenience.`,
      },
    ],
  },
  {
    id: 'systems',
    label: 'Systems',
    sections: [
      {
        title: 'Identity & avatar',
        body: `Your <b>Passport</b> is a permanent username + a weekly-changeable display name + a
        standardized, portable avatar (body template, canonical skeleton, material slots, attachments).
        Avatar <b>complexity</b> is computed server-side. Your appearance propagates to every world
        instantly — the "recognizable stranger". Edit it with <b>P</b>.`,
      },
      {
        title: 'Building interiors (E at the door)',
        body: `Living quarters, civic halls, and hall-scale buildings — plus your own placed houses
        and guild halls — can be <b>entered</b>: walk to the door and press <kbd>E</kbd>. Interiors are
        real spaces inside the building's true footprint: <b>tall towers carry multiple floors</b>
        (stairs climb the west wall), and the <b>windows are real openings</b> — look out and you see
        the exact street you walked in from, live. <kbd>E</kbd> at the door steps back outside.
        Industrial structures (mines, extractors, rigs) and small props have no interior. Townsfolk
        homes arrive furnished — their smiths craft the pieces from the same slate you do.`,
      },
      {
        title: 'Furnishing your home (craft → place → pick up)',
        body: `Thirty-four furniture pieces — beds, sofas, tables, wardrobes, lamps, planters,
        kitchens, and more — are <b>crafted like any other item</b> (Craft panel; recipes take timber
        planks, metal ingots, fiber weave, glass panes…). A crafted piece sits <b>in your inventory</b>
        until you place it: stand inside a structure <b>you own or admin</b> and press
        <b>Place here</b> on the item — it drops at your feet, facing your way, and leaves your
        inventory. Press <kbd>F</kbd> near a placed piece to take it back (exact quality and serial
        preserved). City and town buildings you don't own can <b>never</b> be furnished — placement
        needs your own deed. Crafted quality shows in the piece; a master-crafted armchair is the
        same armchair, just… better.`,
      },
      {
        title: 'Ground combat & weapons',
        body: `The wilds bite back: each biome has its own beasts (stalkers, drones, brutes, lurkers,
        wyrms, shrikes), every one introduced by the <b>nameplate over its head</b> — name, nature,
        and health. Your own <b>health bar and action bar</b> sit bottom-center: <b>⚔ / F</b> attacks
        the nearest creature in reach, with the cooldown sweeping over the button. <b>Weapons are
        crafted, never bought</b>: ten guns and five blades across five tiers, each with its own
        damage, speed, and reach. Craft components (barrels, grips, blade billets, focus crystals,
        storm cores) from raw resources at the Workbench (<b>F6</b>), then assemble the weapon —
        better designs demand deeper component trees and rarer ores, and the <b>quality you craft at
        scales the damage</b>. Click a weapon in the action bar to equip it (it rides on your hip);
        click again to unequip. Salves and rations from the same workbench keep you standing. And the wilds aren't the
        only fight: when two settlements go to war, their <b>militias clash on the road</b> — real
        skirmishes with a battle banner, sparks, and real casualties. You'll get a ⚔ alert when one
        erupts nearby; walk over and watch (or keep your distance).`,
      },
      {
        title: 'Talking to townsfolk (T)',
        body: `Stand near any townsperson and press <kbd>T</kbd>. The conversation window is
        topic-based (the Morrowind lineage): the <b>topic list</b> runs down the right — click one and
        they answer instantly from what is actually true in the sim: what they're doing right now,
        the named building they're standing at, real prices, the levy, offices, and the war if there is
        one. <b>Blue words</b> in their answers are links — click one to pursue it, and gold links add
        brand-new topics to your list (they persist between conversations). <b>Latest rumors</b> repeats
        hearsay they personally picked up from caravans and neighbors — a town with no trade partners
        stays ignorant. The <b>disposition bar</b> (top right) is that town's regard for you: earned by
        honest trade, residency, and conversation, slowly forgotten, and it changes both greetings and
        answers. You can also just type — plain questions ("any rumors?", "who's in charge?", typos and
        all) land on topics instantly; anything deeper goes to the town AI when one is attached, and
        <b>Whisper a tale…</b> plants a rumor that will travel and mutate on its own.`,
      },
      {
        title: 'Conversations that go both ways — questions, stories, tales and leads',
        body: `Townsfolk don't only answer. After the greeting one may <b>bring something up</b>: news, a rumor,
        a grievance — or a <b>question for you</b>. Reply with the choices under their line: what you say of
        yourself (where you're from, what you do, why you came) is <b>remembered</b>, called back on later
        visits, and <b>gets round the town</b> within a day. Change the subject instead and they'll notice
        that too. <i>How</i> you say it matters: a blunt answer lands well on the bold and grates on the
        gentle; a joke is wasted on someone mid-shift.<br><br>
        <b>Their story</b> asks about their life — one chapter a day, built from who they really are
        (birthplace, trade, household, the worst day they've seen); the later chapters are kept for friends.
        <b>Trade a tale…</b> opens your <b>satchel</b>: rumors told to your face and stories told first-hand
        collect there (★ = from someone who was there). Tell the tale their mood wants — they'll say what that
        is — and a good telling earns one back. One a day.<br><br>
        When someone passes on a rumor, ask <b>"Who told you that?"</b> They'll name their source and how far
        they trust it, and a <b>lead</b> goes in your Journal. Find that person — often in another town — and
        <b>Ask about: …</b> appears in their topic list. Keep following the trail and you may reach the one who
        actually <i>saw</i> it, and hear it as it happened. Trails can also go cold: people forget.<br><br>
        Some will tell you where they <b>stand</b> — on the levy, on the keeper. Agree, disagree, or stay out
        of it. Honest disagreement is never held against you, and on the levy a good argument genuinely
        shifts their politics a little: you're one more voice at the canteen. The <b>Journal…</b> entry in the
        topic list keeps your leads, your tales, and what you've told people about yourself. One conversation
        a day with each person builds the relationship, however you spend it.`,
      },
      {
        title: 'Mining rigs — manufacture the deed, place the rig',
        body: `Extractors are <b>manufactured, not granted</b> (the harvester tradition): the Workbench
        (<b>F6</b> → Craft) lists three rig schematics — <b>Field</b>, <b>Industrial</b>, and
        <b>Heavy Extractor Rig</b> — built from alloy plates, circuit lattices, and power cells (the
        heavy rig demands a storm core). Crafting one doesn't mint an item: it manufactures a
        <b>packed DEED in your holdings</b>, and the quality you craft at arrives <b>banked as
        maintenance</b> — a master-built rig shows up fueled. Place the deed like any structure (the
        footprint ghost shows green/red ground; rigs need open industrial ground and a free <b>lot</b>),
        then work it from <b>F6 → Rigs</b>: survey where a resource runs rich first (your <b>droid's
        scan</b> maps concentrations too), because yield tracks the concentration under the rig. Feed
        its maintenance pool, slot an <b>Extraction Rig Core</b> to upgrade it, empty the hopper, and
        <b>redeed</b> to pack it back into a deed and move it. Deeds list on the bazaar like anything
        else — buy a rig another settler built.`,
      },
      {
        title: 'Vehicles — hovercraft & ship flight (X)',
        body: `<b>Hovercraft</b> are crafted machines: five models across five tiers (Dust Skiff →
        Storm Tempest), assembled at the Workbench (<b>F6</b>) from grav coils, alloy hulls, and — at
        the top tier — a storm core. Speed rises with tier and <b>the quality you craft at scales it
        further</b>; every machine lists and sells over the bazaar like any other crafted good. Press
        <kbd>X</kbd> in the open (or the inventory <b>Ride</b> button) to step aboard — it skims over
        terrain and open water alike — and <kbd>X</kbd> again to step off. <b>Your ship flies here
        too</b>: press <kbd>Shift+X</kbd> to recall it (it sets down on clear ground beside you — never
        inside a settlement, never on water), then <kbd>X</kbd> beside the hull to board and lift off.
        It flies with the <b>sector's own stick</b> and your ship's <b>real stats</b>
        (chassis × fitted components): <b>W/S</b> set a held throttle, the <b>mouse steers the
        nose</b>, <b>A/D</b> roll, <b>R</b> levels the wings, <b>SHIFT</b> burns the booster, a steady
        full throttle winds up <b>overcruise</b>, and <b>SPACE</b> (or click) fires the capacitor-fed
        cannons — strafing runs hurt real wilds creatures, loot and cull contracts included. Mind the
        world: the <b>ground and settlement skylines are solid</b> — hard impacts eat shield then
        hull, and a breached hull <b>crashes</b> (the wreck settles on the nearest lawful ground and
        a recovery fee applies, like an escape pod). <kbd>X</kbd> low over legal ground lands — your
        ship then <b>stays parked where you left it</b>, visible to everyone. City airspace refuses
        landings (use the pads). And if you <b>keep climbing</b>, the sky thins around 1,200 m and
        hands you to the space sector — the same doors as a starport launch, return included.`,
      },
      {
        title: 'Droids — deploy & program (K)',
        body: `Crafted droids do more than follow you: <b>Deploy</b> one from the inventory (<b>F6</b>)
        and it becomes a persistent unit standing in the world. Walk up to it and press <kbd>K</kbd>
        for its <b>terminal</b>: immediate orders (Scan, Scan life, Come, Follow, Home, Stop, Recall)
        and a <b>Droidscript</b> editor — a tiny language you program it with:
        <code>goto x z</code> · <code>move n|s|e|w meters</code> · <code>patrol radius</code> ·
        <code>scan</code> / <code>scan life</code> · <code>wait seconds</code> · <code>home</code> ·
        <code>say text</code> · <code>loop</code>. Upload a program and the droid keeps running it
        wherever you wander — patrolling its ground, <b>surveying mineral concentrations</b> (the same
        fields your extractors mine), noting wildlife, and writing everything into its log for you to
        read later. Droids are strictly observers: they can move, look, and report, never touch. They
        stay within 600 m of their deploy point (following you moves that anchor along), at most three
        deployed at once, and <b>Recall</b> packs the exact machine — quality and serial — back into
        your inventory from any distance.`,
      },
      {
        title: 'Economy — credits (ⓒ)',
        body: `Everything runs on one currency: <b>credits</b> (<b>ⓒ</b>), shown top-center in the HUD.
        Every movement goes through a <b>double-entry ledger</b> (dupe-proof by construction); the
        marketplace is fixed-price (95% seller / 5% tax), no gambling. You earn credits from bounties,
        duty contracts, exploration caches, hauling, the bazaar, and events — and spend them on hulls,
        hosting, tickets, market lots, and repairs.`,
      },
      {
        title: 'Bazaar terminals',
        body: `Every settlement's <b>market plaza</b> has a bazaar terminal (walk up and press
        <kbd>B</kbd>). It trades <b>resources, components, and packed structure deeds</b> between
        players (listings are escrowed — the item leaves your hold when listed, the seller is paid 95%
        on sale), and it carries <b>versona stalls</b>: goods the town's own merchants put up at retail.
        Buying from a stall pays that merchant's <i>personal</i> wallet — your credits enter the living
        economy. Deeds bought here can be redeployed on the ground <i>or</i> unfolded in space as
        platforms (see <i>Space Industry</i>).`,
      },
      {
        title: 'Social Pulse, Compass & Orbits',
        body: `Your <b>Pulse</b> is four needs (Social/Fun/Creativity/Rest) that gently decay and are
        lifted by visiting venues — never punitive, just an invitation. The <b>Compass</b> (<b>C</b>) is
        an opt-in visible Want + Fear that makes strangers legible; a mutual <b>Wave</b> between
        compatible Wants within 10 m grants a small Social boost. <b>Orbits</b> (<b>O</b>) are transient,
        auto-formed proximity groups that travel with you across worlds once validated by a real
        interaction.`,
      },
      {
        title: 'Reputation & trust',
        body: `Four server-computed scores — Trustworthiness, Creator Rating, Social Capital, Notoriety —
        that are "relationships, not stats" (no client can set them). You <b>Vouch</b> for others by
        staking your own non-transferable Influence (weighted by your standing); betrayal of a real
        collaborator triggers a −30 Trust hit, a 30-day Traitor tag, and a cascade that forfeits your
        vouchers' stakes. Reviews (incl. Caution flags) are weighted, never hidden.`,
      },
      {
        title: 'Worlds, hosting & World Logic',
        body: `<b>The Forge</b> lets you host worlds (a SOV sink — no port-forwarding) across
        Persistent/Ephemeral/Seasonal/Legacy lifecycle tiers, with the Permission Matrix and a
        jurisdiction/quarantine layer for safety. <b>World Logic</b> (<b>L</b>) is node-based visual
        scripting that compiles to a signed, sandboxed <i>Behavioral Contract</i> — capability-gated,
        CPU/memory-budgeted (it throttles rather than bans), and world-scoped. Prefer text? The
        <b>Lua IDE</b> (<kbd>I</kbd>) writes the same contracts as code — one runtime, two authoring
        surfaces.`,
      },
      {
        title: 'Events, groups & discovery',
        body: `<b>Events</b> can be ticketed (escrowed in SOV) and recurring; a recurring event earns
        <b>ritual</b> status and a deterministic NPC host that remembers regulars. <b>Groups</b> are
        player guilds with a Charter, a ledger-mirrored Treasury, spend votes, embezzlement penalties,
        and fork mechanics. The <b>Memory Book</b> archives lineage, events, and milestones. Discovery
        surfaces hot worlds; the <b>Arcade</b> offers matchmade competitive play with leaderboards.`,
      },
    ],
  },
  {
    id: 'space',
    label: 'Space & Fleets',
    sections: [
      {
        title: 'Reaching space & flying',
        body: `Stand at a <b>city spaceport</b> and press <kbd>Y</kbd> to launch into the sector. The
        flight model is throttle-based: <kbd>W</kbd>/<kbd>S</kbd> set a <i>held</i> throttle, the mouse
        steers pitch and yaw, <kbd>A</kbd>/<kbd>D</kbd> and <kbd>Q</kbd>/<kbd>E</kbd> <b>roll</b>
        (rolling into a turn tightens it), <kbd>Shift</kbd> burns the booster. Hold full throttle with
        steady hands and the engines wind up to <b>overcruise ×10</b>. <kbd>F</kbd> matches your
        target's speed. <kbd>N</kbd> cycles nav points and <kbd>J</kbd> engages the <b>hyperdrive</b>
        for in-system jumps. <kbd>X</kbd> lands at stations, your descent beacon, or any charted world
        planet you're close to.`,
      },
      {
        title: 'Space combat',
        body: `<table>
        <tr><td><kbd>Space</kbd>/<kbd>Click</kbd></td><td>Twin bolts off the weapon capacitor (it recharges)</td></tr>
        <tr><td><kbd>Tab</kbd></td><td>Cycle targets — brackets + a <b>lead pip</b> (aim at the pip, not the hull)</td></tr>
        <tr><td><kbd>1</kbd>–<kbd>4</kbd></td><td>Power presets: weapons · shields · engines · balanced</td></tr>
        <tr><td><kbd>R</kbd></td><td>Fire a homing missile (rearm rides <kbd>G</kbd> repair)</td></tr>
        <tr><td><kbd>C</kbd></td><td>Countermeasure flares (break seekers)</td></tr>
        <tr><td><kbd>T</kbd></td><td>Subsystem targeting — engines (slow) or weapons (jam)</td></tr>
        <tr><td><kbd>V</kbd></td><td>Droid emergency hull patch (needs a droid port + droid)</td></tr>
        <tr><td><kbd>G</kbd></td><td>Repair &amp; rearm at a station (fee) — or FREE at your own depot</td></tr>
        </table>
        Shields split <b>front/rear</b> — hits drain the facing arc before the hull. Hull at zero fires
        your <b>escape pod</b> home (small recovery fee). Every raider kill pays a bounty and rolls for
        <b>component loot</b>; named <b>aces</b> hold the fortress dens.`,
      },
      {
        title: 'Your fleet — ships & fitting',
        body: `Press <kbd>P</kbd> near a station for the dock: <b>Shipyard</b> (five chassis — Lancer,
        Talon, Courier, Bulwark, and the walkable Sovereign), <b>Hangar</b>, and <b>Loadout</b> with ten
        hardpoints (reactor, engine, capacitor, shield, armor, weapon, missile, counter, droid port,
        cargo). Fitting is budgeted: every module draws <b>CPU</b> and <b>POWERGRID</b>, better gear
        draws more, and each chassis has fixed output — <i>you cannot max every slot</i>. The Loadout
        tab shows both bars and each module's draw. Components come from pirate loot, wreck salvage,
        derelict sites, crafting, and the bazaar. <kbd>B</kbd> boards a station on approach (docking
        bay → corridor → promenade); <kbd>I</kbd> walks the Sovereign's own deck — a real hull with
        <b>viewports onto the live sector</b> (bridge glass wraps the fore) that ships as a
        <b>bare, furnishable space</b>: press <kbd>E</kbd> on the deck to place command chairs,
        consoles, holotables, bunks, reactors, and more where you stand (up to 24 pieces, persistent
        per ship — your layout follows your Sovereign).`,
      },
      {
        title: 'Duty boards & agents',
        body: `The dock's <b>Duty</b> tab is the contract board: <b>Patrol</b>, <b>Sweep</b>, and
        <b>Strike</b> (strikes mint battle tokens; 3 tokens open a component crate). Higher boards open
        as your standing grows — <b>Vanguard</b> at rank 2, <b>Blockade</b> at rank 3, and the elite
        <b>Crucible</b> at rank 4 with Trustworthiness 60+. Claim pay scales <b>+15% per rank</b> (and
        +20% for sworn pilots). Swear to the <b>Aurelian Concord</b> or <b>Borean Compact</b> at the
        dock for the allegiance dividend and a five-step career arc — but the enemy civ's wings will
        mark you in open space.`,
      },
      {
        title: 'FLEET WARS',
        body: `Every hour the two civs <b>war for 30 minutes</b> over a rotating fortress (a line
        retreats no earlier than 15 minutes in — the server's engagement law): ten
        <b>4.5 km capital hulls</b> and <b>thirty gunships per side</b> warp in across the opening
        seconds, then broadsides, torpedoes and attrition explosions carry the line. The HUD counts
        down to the next war and <kbd>M</kbd> charts the live one (⚔). <b>The war reads you:</b> an
        unaligned hull is hostile to BOTH battle lines; sworn pilots are marked only by the enemy civ.
        Capital hulls have real collision; your bolts and missiles <b>damage them</b> — burn one down
        and it dies in staged blasts, sheds hulks onto the field, and pays a tier-3 bounty. The
        gunships are real ships flying the pirate AI: shoot one and it comes for you (they pay no
        bounty). Hostile gunships peel off the screen to intercept you, and the capital walls volley
        anyone they read as an enemy. Every war kill <b>pushes the front</b>: it shifts control of the
        star system this war decides (see <i>Sovereignty</i> in the Space Industry tab). When the
        guns fall silent the field becomes a <b>salvage yard</b> — fly it with the utility beam before
        the cycle rolls. <b>Docking and travel:</b> <kbd>P</kbd> docks you — the server checks you
        are at the station, and markets, haulage, repair and the shipyard work only while docked;
        hyper-warp and sublight courses are orders the server completes, and a course keeps running
        while you are logged out. The Commons lies below the Commons Reach only — from any other sky,
        dock or warp home first. <b>The war is a server campaign:</b> the clock you see renders a
        <i>space_campaign</i> row — its sides, its reserves, its rounds — so every pilot sees the same
        war; a kill is a receipt the server validates, and the winning side's receipts pay credits and
        shift control of the system the war decides (see <i>The campaign</i>).`,
      },
      {
        title: 'Crewing a Sovereign (Shift+B)',
        body: `A <b>Sovereign</b> carries a second seat: its <b>dorsal turret</b>. Fly within 3 km of a
        friend's Sovereign in the same sky and press <kbd>Shift+B</kbd> to take it — your own hull
        vanishes, you ride their hull wherever it flies, you aim with the mouse and fire as usual, and
        your bolts count as yours (dens, raiders, the wardec). <kbd>Shift+B</kbd> again gives the seat
        up; docking, a hyper-warp or a lost link ends it. One seat per Sovereign; the captain sees who
        mans it.`,
      },
      {
        title: 'The galaxy — charts, warp & courses',
        body: `<kbd>M</kbd> is the <b>solar chart</b> (planets, stations, dens, the live war);
        <kbd>G</kbd> away from a station opens the <b>galactic chart</b> — the Commons Reach plus 100
        prebuilt systems on the spiral, and one system for every public player world. Danger and riches
        grow <b>rimward</b> (tier ☠ to ☠☠☠). Travel two ways: <b>hyper-warp</b> — a 30-second crossing
        inside a visible warp tunnel — or <b>set course</b> at sublight, which takes real hours-to-days
        of wall-clock time and keeps running while you're logged out. The chart also shows the
        <b>sovereignty rings</b> (gold Concord / ice Compact), ☠ <b>incursion halos</b>, and dashed
        <b>wormhole crossings</b>.`,
      },
      {
        title: 'Squadrons (K)',
        body: `<kbd>K</kbd> opens the OPS panel. Your P9 <b>Group</b> is your squadron: see every
        member with distance, <b>Warp to</b> any of them (a nav point + the standard hyper spool), and
        talk on the <b>fleet channel</b> (member-gated). Squadron kills at a den <b>advance every
        member's matching duty contract</b> — fly together, progress together. The panel also manages
        your deployed <b>platforms</b> (see Space Industry).`,
      },
    ],
  },
  {
    id: 'industry',
    label: 'Space Industry',
    sections: [
      {
        title: 'Cargo has a place',
        body: `Space goods live in ONE place at a time. Mined ore, salvage and moon chunks land in your
        <b>active hull's hold</b> (Lancer 100 u · Talon 50 · Bulwark 200 · Courier 600 · Sovereign 1,000; a
        cargo component adds more) — a full hold stops the beam. Exchange purchases and order fills land in
        <b>your warehouse at that station</b>: open the dock's <b>Hold</b> tab to load them aboard before you
        fly, and unload what you want to keep there. A courier crate rides in your hold, tagged, and goes
        down with the ship: a lost hull leaves a <b>wreck</b> at her last position that ANY pilot within
        1.5 km can strip with the utility beam. Landing (X, or a charted world) unloads your hold into your
        bag; <kbd>L</kbd> at a Commons spaceport loads your market goods for the trip out. Nothing else
        crosses between the ground and the sky.`,
      },
      {
        title: 'The lane',
        body: `The Commons Reach runs a real supply chain you can protect or plunder. <b>Aurel Mining</b> works
        the <b>Kessel Run Scar</b> — a finite belt that renews slowly and that your own beam draws from too
        (the HUD shows what is left) — and each hour it holds 60 u it sends an <b>ore run</b> from Waystation
        Aurel to <b>Aurel Prime Yards</b>. Mid-lane the nearest pirate den sends up to three of its own raiders —
        real hulls, drawn from the den's shared population — to take the run: kill them for bounties inside 4 km,
        and the last one down beats the party; two or more left when the fight ends take the run, leaving a wreck
        any pilot can strip, and the survivors go home to their den. Clear the dens and the lane runs clean. Fly
        within 4 km and press <kbd>E</kbd> to <b>escort</b> it — an escort kills a raider every half minute and the
        miner pays you at arrival. The yard turns ore
        into hulls; short of ore it raises its ore price at Aurel Prime and posts <b>supply work</b> on the
        dock's Work tab that you deliver there, docked. Its hulls staff the Aurelian patrol over Aurel Prime.
        The ops panel (<kbd>K</kbd>) reads the whole lane.`,
      },
      {
        title: 'The campaign',
        body: `Every fleet war is a <b>campaign</b> the server runs: each civ commits capitals and gunships from a
        <b>finite reserve</b> (the Concord's is fed by Aurel Prime Yards), the lines grind each other down round by
        round, and a side down to three capitals <b>retreats</b>. Kills you make inside the war zone become
        <b>receipts</b> (a gunship one point, a capital ten) on your side's ledger; when your side holds the field the
        campaign's objective system leans your way on the chart and your receipts pay 25 ⓒ a point. Survivors go home
        to the reserve; losses are gone for good. An <b>incursion</b>'s grip is shared too — every raider you kill in
        an occupied system counts once for everyone. The ops panel (<kbd>K</kbd>) reads the campaign and the reserves.`,
      },
      {
        title: 'What you know',
        body: `The galaxy chart shows what has been <b>observed</b>, not everything that is. Arriving in a system,
        recovering a signature and docking each file an observation — who saw it, under which banner, when — and the
        chart tints, skulls and wormhole lines only what your own and your civ's observations support, marked
        <b>fresh</b> (under two hours), <b>stale</b> (under a day) or <b>unseen</b>. The system you are in is always
        live. A sworn pilot can <b>file a scout report</b> from the ops panel (<kbd>K</kbd>): fresh news pays 40 ⓒ
        × the system's tier and banks loyalty; repetition within two hours pays nothing, and the Commons is always
        known. <b>Bookmarks</b> mark a position with a label, private or shared with your civ — <kbd>N</kbd> cycles
        them with the nav targets, and the ops panel lists them.`,
      },
      {
        title: 'One state, four resolutions',
        body: `Every system is simulated at a resolution: <b>L0</b> hull by hull while a pilot is in it, <b>L1</b> held
        warm for five minutes after the last pilot leaves, <b>L2</b> advanced only on demand while someone's intel on
        it is fresh, <b>L3</b> cold. Pirate dens are one shared population per den: every kill anyone makes takes a
        raider off it, a cleared den refuses further bounties, and it refills one raider every two minutes — the same
        arithmetic whether a pilot is watching (tick by tick) or the den is cold (all at once when someone arrives). A
        den you cleared is still cleared when you come back later, plus what refilled. The ops panel (<kbd>K</kbd>)
        reads the system's resolution and every den's count.`,
      },
      {
        title: 'Mining (hold U)',
        body: `Every system's <b>asteroid fields</b> carry ore seams. Fly inside a field and <b>hold
        <kbd>U</kbd></b> — the beam locks the seam and extracts on ~4 s cycles, minting <b>stat-rolled
        resource stacks</b> straight into your hold (they are real crafting inputs, same families as
        ground resources). Rare <b>rich pockets</b> double a cycle. The seams are tier-banded — richer
        ore only spawns rimward: <b>Drift Ferrite</b> and <b>Pale Silica</b> everywhere; <b>Cobalt
        Shale</b> and <b>Aether Ice</b> from tier-2 systems; <b>Voidglass Lodes</b> and <b>Noble
        Seams</b> only in tier-3 space. The 100 prebuilt systems are worth prospecting.`,
      },
      {
        title: 'Refining',
        body: `Raw ore refines into tighter stock: the station dock's <b>Industry</b> tab runs the
        public line at <b>35%</b> yield; your own deployed <b>refinery beacon</b> runs <b>50%</b>
        (fly within ~2.5 km of it — the server checks). Refined stock carries boosted stat rolls and
        feeds the same crafting schematics, versona stalls, and bazaar listings as any resource.`,
      },
      {
        title: 'Wreck salvaging',
        body: `Fleet wars litter the field with <b>wrecks</b> — dozens of fighter hulls and a handful
        of broken capitals that linger until the war cycle rolls (about ten minutes of salvage window).
        Hold <kbd>U</kbd> near a wreck: the cutter strips <b>Hull Scrap</b> and <b>Circuit Salvage</b>
        every cycle, with a chance of an intact <b>component</b> — capital wrecks strip far richer
        (about half yield a component). Each wreck strips <b>once per pilot</b>. Salvaging DURING the
        war is possible, and exactly as dangerous as it sounds.`,
      },
      {
        title: 'Exploration scanning (O)',
        body: `Every system hides <b>1–3 signatures</b> that re-seed every few hours, and every open
        <b>wormhole throat</b> is hidden the same way — where they are is the <i>server's</i> secret,
        never your chart's. Press <kbd>O</kbd> to ping: the server narrows the <i>nearest</i> unresolved
        site from where you are (distance + bearing), and the third ping resolves it into a marked
        <b>beacon</b> (a throat's mouth appears only then). Fly to the beacon and hold
        <kbd>U</kbd> to recover it: <b>caches</b> pay credits (scaled by system tier), <b>derelicts</b>
        yield an intact component, and rare <b>relic vaults</b> add a bundle of tier-3 crystal stock.
        Each signature recovers once per pilot — sweep a system clean and move on.`,
      },
      {
        title: 'Wormholes',
        body: `A few <b>wormholes</b> connect random pairs of systems each epoch — dashed crossings on
        the galactic chart, a swirling blue throat in space. Fly into the throat and it takes you to
        the far end <b>instantly and free</b> — the lucky counterpoint to the 30 s warp and multi-day
        courses. They re-pair every few hours; a good crossing is worth planning a haul around.`,
      },
      {
        title: 'Regional exchange & hauling',
        body: `Every station dock has an <b>Exchange</b> tab. Prices <b>drift per station</b> on a
        two-hour cycle, and geography matters: <b>core systems sell basics cheap</b> (glutted), while
        <b>rim stations pay premiums</b> for staples — and go cheap on luxuries they mine themselves.
        Buy low, fill the hold, warp rimward, sell high: the <b>Courier</b> chassis finally has its
        job. Mined ore and salvage sell on the same exchange (their families bridge to commodities), so
        a mining run can cash out anywhere.`,
      },
      {
        title: 'Sovereignty — the front line',
        body: `Every charted system leans to a civ; the galactic chart rings each one <b>gold
        (Concord)</b> or <b>ice (Compact)</b>, and arrival toasts name the holder. Each fleet war
        <b>decides a specific system</b> (the HUD and OPS panel name it): killing a side's war gunship
        contributes to the <i>opposing</i> civ's grip there, and enough contributions <b>flip
        control</b>. Control is persistent and shared across all pilots — the wars write the map.`,
      },
      {
        title: 'Incursions',
        body: `Every few hours a <b>raider armada occupies</b> two mid/rim systems — a pulsing ☠ halo
        on the galactic chart. Inside, den spawns run <b>×3</b> and a <b>warlord</b> (heavy hull, ☠
        target name) anchors the first den. The HUD tracks the raiders' <b>grip</b>: every kill pushes
        it back, the warlord's death breaks it outright. Triple the targets means triple the bounties,
        loot rolls, and duty progress — incursions are the richest hunting in the galaxy while they
        last.`,
      },
      {
        title: 'Deployable platforms',
        body: `A <b>packed structure deed</b> (redeed a building, or buy one at a bazaar terminal)
        unfolds in space into a personal platform via the <kbd>K</kbd> panel: a <b>depot</b> — free
        repair and rearm within 600 m (<kbd>G</kbd>), your forward base — or a <b>refinery beacon</b> —
        the 50% refine line, the anchor of a deep-space mining camp. Two platforms per pilot;
        <b>Scoop</b> one (fly close) and it folds losslessly back into the deed.`,
      },
    ],
  },
];

// ─── Module ───────────────────────────────────────────────────────────────────────

export class AtlasHelpModule implements IModule {
  readonly name = 'AtlasHelp';
  private overlay: HTMLDivElement | null = null;
  private tabBar: HTMLDivElement | null = null;
  private contentEl: HTMLDivElement | null = null;
  private searchInput: HTMLInputElement | null = null;
  private activeTab = TABS[0].id;
  private query = '';

  init(_app: App): void {
    this.buildButton();
    this.buildOverlay();
    window.addEventListener('keydown', (e) => {
      const tag = (e.target as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') { if (e.key === 'Escape') this.close(); return; }
      if (e.key === 'h' || e.key === 'H') { e.preventDefault(); this.toggle(); }
      else if (e.key === 'Escape' && this.isOpen()) this.close();
    });
    // The welcome popup's button (and any module) opens the manual through the bus.
    bus.on('atlas:open-help', () => this.open());
  }

  /** Public open (the welcome window, probes). */
  openHelp(): void { this.open(); }

  private isOpen(): boolean { return !!this.overlay && this.overlay.style.display !== 'none'; }
  private toggle(): void { this.isOpen() ? this.close() : this.open(); }
  private open(): void { if (this.overlay) { this.overlay.style.display = 'flex'; this.render(); this.searchInput?.focus(); } }
  private close(): void { if (this.overlay) this.overlay.style.display = 'none'; }

  private buildButton(): void {
    const btn = document.createElement('button');
    btn.textContent = '?';
    btn.title = 'Help & manual (H)';
    btn.style.cssText =
      'position:fixed;right:14px;bottom:14px;z-index:90;width:38px;height:38px;border-radius:50%;' +
      'background:rgba(20,26,38,0.92);color:#9cf;border:1px solid #3a5170;font:700 18px system-ui;' +
      'cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,0.4)';
    btn.addEventListener('click', () => this.toggle());
    document.body.appendChild(btn);
    // HUD LADDER (2026-08-24 overlap audit): in space the sensor radar owns
    // the bottom-right corner (148 px, right:16 bottom:16) — the "?" sat on
    // top of it. Ride above the radar while the space HUD is up.
    let inSpace = false;
    window.setInterval(() => {
      const now = atlas.spaceFlight || !!document.getElementById('space-hud');
      if (now === inSpace) return;
      inSpace = now;
      btn.style.bottom = now ? '316px' : '14px'; // above the radar (16..164) AND the pulse gauges (200..308)
    }, 500);
  }

  private buildOverlay(): void {
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:120;display:none;align-items:center;justify-content:center;' +
      'background:rgba(0,0,0,0.55);font:13px/1.5 system-ui,sans-serif';
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.close(); });

    const win = document.createElement('div');
    win.style.cssText =
      'width:min(860px,92vw);height:min(640px,88vh);display:flex;flex-direction:column;' +
      'background:#0e1119;color:#cdd6e4;border:1px solid #2a3346;border-radius:10px;overflow:hidden;' +
      'box-shadow:0 12px 48px rgba(0,0,0,0.6)';

    // Header: title + search + close
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid #2a3346';
    const title = document.createElement('div');
    title.textContent = 'Cosmos — Help & Manual';
    title.style.cssText = 'font-weight:700;color:#fff;white-space:nowrap';
    const search = document.createElement('input');
    search.type = 'search';
    search.placeholder = 'Search everything…  (H to toggle · Esc to close)';
    search.style.cssText =
      'flex:1;background:#11151f;color:#dfe;border:1px solid #33405a;border-radius:6px;padding:6px 10px;font:13px system-ui';
    search.addEventListener('input', () => { this.query = search.value.trim().toLowerCase(); this.render(); });
    this.searchInput = search;
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = 'background:none;border:none;color:#8a97ab;font:16px system-ui;cursor:pointer;padding:2px 6px';
    closeBtn.addEventListener('click', () => this.close());
    header.append(title, search, closeBtn);

    // Body: tab sidebar + content
    const body = document.createElement('div');
    body.style.cssText = 'flex:1;display:flex;min-height:0';
    const tabBar = document.createElement('div');
    tabBar.style.cssText = 'width:172px;flex-shrink:0;border-right:1px solid #2a3346;overflow-y:auto;padding:8px 0';
    const content = document.createElement('div');
    content.style.cssText = 'flex:1;overflow-y:auto;padding:14px 18px';
    body.append(tabBar, content);

    win.append(header, body);
    overlay.appendChild(win);
    document.body.appendChild(overlay);
    this.overlay = overlay;
    this.tabBar = tabBar;
    this.contentEl = content;
    this.renderTabs();
  }

  private renderTabs(): void {
    if (!this.tabBar) return;
    this.tabBar.replaceChildren();
    for (const tab of TABS) {
      const b = document.createElement('button');
      b.textContent = tab.label;
      const active = tab.id === this.activeTab && !this.query;
      b.style.cssText =
        'display:block;width:100%;text-align:left;background:' + (active ? '#1c2740' : 'none') + ';' +
        'color:' + (active ? '#bcd8ff' : '#9aa7bd') + ';border:none;border-left:3px solid ' +
        (active ? '#4a86ff' : 'transparent') + ';padding:7px 14px;font:13px system-ui;cursor:pointer';
      b.addEventListener('click', () => { this.activeTab = tab.id; this.query = ''; if (this.searchInput) this.searchInput.value = ''; this.render(); });
      this.tabBar.appendChild(b);
    }
  }

  private render(): void {
    this.renderTabs();
    if (!this.contentEl) return;
    this.contentEl.replaceChildren();

    if (this.query) {
      const results = this.search(this.query);
      const heading = document.createElement('div');
      heading.style.cssText = 'color:#8a97ab;margin-bottom:10px';
      heading.textContent = results.length ? `${results.length} result(s) for "${this.query}"` : `No results for "${this.query}"`;
      this.contentEl.appendChild(heading);
      for (const r of results) this.contentEl.appendChild(this.sectionEl(r.section, r.tabLabel));
      return;
    }
    const tab = TABS.find((t) => t.id === this.activeTab) ?? TABS[0];
    for (const s of tab.sections) this.contentEl.appendChild(this.sectionEl(s));
  }

  private sectionEl(s: HelpSection, tabLabel?: string): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'margin-bottom:16px';
    const h = document.createElement('div');
    h.style.cssText = 'font-weight:700;color:#eaf1ff;margin-bottom:5px';
    h.textContent = tabLabel ? `${s.title}  ·  ${tabLabel}` : s.title;
    const b = document.createElement('div');
    b.style.cssText = 'color:#c2ccdc';
    this.setHtml(b, s.body); // s.body is trusted static content authored in TABS
    wrap.append(h, b);
    return wrap;
  }

  /**
   * Render trusted static HTML without touching a live element's innerHTML. DOMParser
   * builds an inert document (scripting disabled — no <script>/onload/etc. ever runs),
   * and we append the parsed nodes. Safe even though this content is author-controlled.
   */
  private setHtml(el: HTMLElement, html: string): void {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    el.append(...Array.from(doc.body.childNodes));
  }

  private search(q: string): Array<{ section: HelpSection; tabLabel: string }> {
    const out: Array<{ section: HelpSection; tabLabel: string }> = [];
    for (const tab of TABS) {
      for (const s of tab.sections) {
        const plain = (s.title + ' ' + s.body.replace(/<[^>]+>/g, ' ')).toLowerCase();
        if (plain.includes(q)) out.push({ section: s, tabLabel: tab.label });
      }
    }
    return out;
  }

  dispose(): void {
    this.overlay?.remove();
  }
}
